/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { VpnKey } from "@mui/icons-material";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { DB_PATCH_REPO_NAME } from "../../../../utils/constants";
import { githubUrl, simUrl } from "../../../../utils/urls";
import useIamModal from "../../../IamModal/UseIamModal";
import { tokens } from "../../../../theme";
import { buildDetailedPanelStyles, buildDetailsCardStyles } from "../../styles";
import StepBox from "./StepBox";
import {
  downloadFileFromUrl,
  toPascalCase,
} from "../../../../utils/helperFunctions";
import ResultBox from "../../../Automations/ResultBox";
import { complileResults } from "../../../Automations/utils";

const STAGE_STATUS_META = {
  COMPLETED: { label: "Passed", dot: "success.main" },
  FAILED: { label: "Failed", dot: "error.main" },
  RUNNING: { label: "Running", dot: "warning.main" },
};

const DetailPanelContent = ({
  row,
  showRetryDialog,
  retryPermissionList,
  setReportModalOpen,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const { openIamModal, canViewIamModal, IamModal } = useIamModal(
    `/apps/tessell/software-images/db-software/builds/${row?.id}`,
    [],
    buildDetailedPanelStyles.otherDetailsButton
  );

  const downloadScript = (stage) => {
    const blob = new Blob([stage.script], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${stage.id}_script.sh`;
    a.click();

    URL.revokeObjectURL(url);
  };

  // Stage rows are data-driven; add a stage here and the table just grows.
  const stages = [
    { id: "installation", name: "Installation" },
    { id: "validation", name: "Validation" },
  ].map(({ id, name }) => {
    const step = row?.runData?.steps?.find(
      (s) => s?.stepName === `run-${id}-script`
    );
    return {
      id,
      name,
      status: step?.status || "PENDING",
      // TODO: render "Passed · 4m 12s" once the builds API exposes step timings.
      durationMs: null,
      script: row?.inputData?.[`${id}Script`],
      logsUrl: `${simUrl}/apps/tessell/software-images/db-software/builds/${row?.id}/${id}-logs`,
      logsReady: Boolean(step && step?.status !== "RUNNING"),
    };
  });

  const failedStage = stages.find((stage) => stage.status === "FAILED");
  const allPassed = stages.every((stage) => stage.status === "COMPLETED");
  const commitDone = row?.runData?.steps?.some(
    (step) =>
      step?.stepName === "commit-snapshot-yaml" && step?.status === "COMPLETED"
  );
  const qaDone = row?.runData?.steps?.some(
    (step) => step?.stepName === "trigger-qa" && step?.status === "COMPLETED"
  );

  const prDisabledReason = failedStage
    ? `${failedStage.name} failed — fix before opening a PR`
    : !allPassed
    ? "All stages must pass before opening a pull request"
    : !commitDone
    ? "Snapshot commit has not completed yet"
    : "";

  const footerSummary = failedStage
    ? `${failedStage.name} failed — fix before opening a PR`
    : allPassed
    ? "Both stages passed — ready to open a pull request"
    : "Stages are still in progress";

  const openPr = () =>
    window.open(
      `${githubUrl}/${DB_PATCH_REPO_NAME}/compare/${
        row?.inputData?.releaseBranch
      }...${
        row?.jiraId
      }_${row?.cloud.toLowerCase()}_convoy_db_software_patch_automation?expand=1`
    );

  const stageStatus = (stage) => {
    const meta = STAGE_STATUS_META[stage.status] || {
      label: toPascalCase(stage.status),
      dot: "grey.400",
    };
    return (
      <Box sx={buildDetailsCardStyles.statusCellBox}>
        <Box sx={buildDetailsCardStyles.statusDot(meta.dot)} />
        <Typography variant="body2">{meta.label}</Typography>
      </Box>
    );
  };

  const artifactButtons = (stage) => (
    <>
      <Tooltip
        title={stage.script ? "" : "Script is not available for this build"}
      >
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={!stage.script}
            onClick={() => downloadScript(stage)}
          >
            Script
          </Button>
        </span>
      </Tooltip>
      <Tooltip
        title={
          stage.logsReady ? "" : "Logs are available once the stage has run"
        }
      >
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={!stage.logsReady}
            onClick={() => downloadFileFromUrl(stage.logsUrl)}
          >
            Logs
          </Button>
        </span>
      </Tooltip>
    </>
  );

  return (
    <Grid container sx={buildDetailedPanelStyles.drawerGrid}>
      {IamModal}
      <Grid xs={12}>
        <Paper elevation={0} sx={buildDetailedPanelStyles.paper(colors)}>
          <Typography sx={buildDetailedPanelStyles.panelSectionLabel(colors)}>
            Build Steps
          </Typography>
          {row?.runData?.steps?.map((step) => (
            <StepBox
              step={step}
              showRetryDialog={showRetryDialog}
              id={row?.id}
              retryPermissionList={retryPermissionList}
            />
          ))}
        </Paper>
      </Grid>
      <Grid xs={12}>
        <Paper elevation={0} sx={buildDetailsCardStyles.paper(colors)}>
          <Box sx={buildDetailsCardStyles.header}>
            <Typography variant="subtitle1" fontWeight={600}>
              Build details
            </Typography>
            <Typography sx={buildDetailsCardStyles.headerVersion}>
              {row?.inputData?.oldPatchVersionName}
            </Typography>
            <Chip label="Previous version" size="small" variant="outlined" />
            <Box sx={buildDetailsCardStyles.headerSpacer} />
            {canViewIamModal && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<VpnKey fontSize="small" />}
                onClick={() => openIamModal()}
              >
                IAM
              </Button>
            )}
          </Box>

          <Box sx={buildDetailsCardStyles.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={buildDetailsCardStyles.headCell}>
                    Stage
                  </TableCell>
                  <TableCell
                    sx={{ ...buildDetailsCardStyles.headCell, width: 130 }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ ...buildDetailsCardStyles.headCell, width: 200 }}
                  >
                    Artifacts
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stages.map((stage) => (
                  <TableRow key={stage.id}>
                    <TableCell sx={buildDetailsCardStyles.stageNameCell}>
                      {stage.name}
                    </TableCell>
                    <TableCell>{stageStatus(stage)}</TableCell>
                    <TableCell>
                      <Box sx={buildDetailsCardStyles.artifactsCellBox}>
                        {artifactButtons(stage)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={buildDetailsCardStyles.stackedWrap}>
            {stages.map((stage) => (
              <Box
                key={stage.id}
                sx={buildDetailsCardStyles.stackedBlock(colors)}
              >
                <Box sx={buildDetailsCardStyles.stackedBlockHead}>
                  <Typography variant="body2" fontWeight={500}>
                    {stage.name}
                  </Typography>
                  {stageStatus(stage)}
                </Box>
                <Box sx={buildDetailsCardStyles.stackedButtons}>
                  {artifactButtons(stage)}
                </Box>
              </Box>
            ))}
          </Box>

          {qaDone && (
            <Box sx={buildDetailsCardStyles.qaRow(colors)}>
              <Typography variant="body2" fontWeight={500}>
                QA Results
              </Typography>
              <ResultBox
                compliedResults={complileResults(row?.runData?.qaResults)}
                sx={buildDetailsCardStyles.statusCellBox}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setReportModalOpen(
                    `${simUrl}/apps/tessell/software-images/db-software/builds/${row?.id}/qa-automation-report`
                  )
                }
              >
                Report
              </Button>
            </Box>
          )}

          <Box sx={buildDetailsCardStyles.footer(colors)}>
            <Typography
              sx={buildDetailsCardStyles.footerSummary(Boolean(failedStage))}
            >
              {footerSummary}
            </Typography>
            <Tooltip title={prDisabledReason}>
              <Box sx={buildDetailsCardStyles.footerButtonWrap}>
                <Button
                  variant="contained"
                  disabled={Boolean(prDisabledReason)}
                  onClick={openPr}
                  sx={buildDetailsCardStyles.footerButton}
                >
                  Create PR
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DetailPanelContent;
